import { RefObject } from "react"
import { ArrowArcLeft, ArrowArcRight } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useHotkeys } from "@/hooks/useHotkeys"
import { cn } from "@/lib/utils"
import { useChatHistoryUndoRedo } from "@/modules/chats/hooks/useChatHistory"
import { useChatInputContext } from "@/modules/chats/stores/useChatInputStore"

type UndoRedoButtonsProps = {
  textareaRef: RefObject<HTMLTextAreaElement | null>
}

export const UndoRedoButtons = (props: UndoRedoButtonsProps) => {
  const { textareaRef } = props

  const isAiRunning = useChatInputContext((store) => store.aiRunning !== "NONE")
  const isSendingMessage = useChatInputContext((store) => store.isSendingMessage)

  const { canUndo, canRedo, undo, redo } = useChatHistoryUndoRedo()

  const isUndoDisabled = isAiRunning || isSendingMessage || !canUndo
  const isRedoDisabled = isAiRunning || isSendingMessage || !canRedo

  const handleUndo = () => {
    if (isUndoDisabled) return
    undo()
    FaroInsightsWrapper.pushEvent("chat_undo")
  }

  const handleRedo = () => {
    if (isRedoDisabled) return
    redo()
    FaroInsightsWrapper.pushEvent("chat_redo")
  }

  useHotkeys("ctrl+z", handleUndo, { ref: textareaRef })
  useHotkeys("ctrl+y", handleRedo, { ref: textareaRef })

  return (
    <>
      <Button
        variant="circle"
        data-testid="undo-chat-input"
        className="h-8 w-8 disabled:bg-transparent"
        onClick={isUndoDisabled ? undefined : handleUndo}
        disabled={isUndoDisabled}>
        <ArrowArcLeft size={24} className={cn({ "text-on-surface-disabled": isUndoDisabled })} />
      </Button>
      <Button
        variant="circle"
        data-testid="redo-chat-input"
        className="h-8 w-8 disabled:bg-transparent"
        onClick={isRedoDisabled ? undefined : handleRedo}
        disabled={isRedoDisabled}>
        <ArrowArcRight size={24} className={cn({ "text-on-surface-disabled": isRedoDisabled })} />
      </Button>
    </>
  )
}
